import React, { useState } from "react";
import { format } from "date-fns";
import {
  CalendarIcon,
  Check,
  ArrowRight,
  User,
  Mail,
  Phone,
  MapPin,
  Users,
  Sparkles,
  PartyPopper,
  Clock,
  Hourglass,
  MoveRight,
  RefreshCw,
  Ticket,
  ShieldCheck,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { useToast } from "../hooks/use-toast";
import { serviceTypes, luxuryAddOns, computeQuote, MIN_HOURS } from "../mock";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_ICONS = { Hourglass, MoveRight, RefreshCw, Ticket, ShieldCheck, Clock };

// Selectable pickup time slots (5:00 AM → 11:30 PM, 30-min steps)
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 5; h <= 23; h++) {
    for (const m of [0, 30]) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const ampm = h < 12 ? "AM" : "PM";
      const label = `${(h % 12) || 12}:${String(m).padStart(2, "0")} ${ampm}`;
      slots.push({ value, label });
    }
  }
  return slots;
})();

const formatTime = (value) => {
  const found = TIME_SLOTS.find((s) => s.value === value);
  return found ? found.label : value;
};

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  time: "",
  pickup: "",
  dropoff: "",
  passengers: "1",
  notes: "",
};

const BookOnline = () => {
  const [form, setForm] = useState(emptyForm);
  const [date, setDate] = useState(null);
  const [addOns, setAddOns] = useState([]);
  const [hours, setHours] = useState(MIN_HOURS);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleAddOn = (id) =>
    setAddOns((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !date) {
      toast({
        title: "Missing details",
        description: "Please fill in your name, email, phone, service and pickup date.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    let booking;
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        date: format(date, "PPP"),
        date_iso: format(date, "yyyy-MM-dd"),
        time: form.time,
        pickup: form.pickup,
        dropoff: form.dropoff,
        passengers: form.passengers,
        add_ons: addOns,
        notes: form.notes,
        hours: isHourly ? hours : 0,
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      booking = {
        ...form,
        date: payload.date,
        addOns: data.add_ons || addOns,
        id: data.reference,
        estimatedPrice: data.estimated_price,
        priced: data.priced,
        hours: data.hours,
      };
    } catch (err) {
      setSubmitting(false);
      toast({
        title: "Something went wrong",
        description: "We couldn't submit your booking. Please try again or call us.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(false);
    setSubmitted(booking);
    toast({ title: "Booking Confirmed 🚗", description: `Reference ${booking.id}. We'll be in touch shortly.` });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setForm(emptyForm);
    setDate(null);
    setAddOns([]);
    setSubmitted(false);
  };

  const isHourly = form.service === "Hourly Rental";
  const quote = form.service ? computeQuote(form.service, hours, addOns) : null;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 pt-28 pb-20">
        <div className="reveal is-visible w-full max-w-lg rounded-3xl border border-[var(--gold)]/30 bg-[#13212e] p-10 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/40">
            <PartyPopper className="text-[var(--gold)]" size={38} />
          </div>
          <h1 className="mt-6 font-display text-3xl font-extrabold text-white">Booking Confirmed!</h1>
          <p className="mt-3 text-muted-foreground">
            Thank you, {submitted.name.split(" ")[0]}. Your luxury ride is reserved.
          </p>
          <div className="mt-6 rounded-xl bg-[#0a121a] border border-white/10 p-5 text-left text-sm space-y-2">
            <Row label="Reference" value={submitted.id} gold />
            <Row label="Service" value={submitted.service} />
            {submitted.priced && submitted.hours > 0 && (
              <Row label="Hours" value={`${submitted.hours} hrs (3 hr min)`} />
            )}
            <Row label="Date" value={submitted.date} />
            {submitted.time && <Row label="Time" value={formatTime(submitted.time)} />}
            <Row label="Pickup" value={submitted.pickup || "—"} />
            <Row label="Passengers" value={submitted.passengers} />
            {submitted.addOns.length > 0 && (
              <Row label="Add-Ons" value={submitted.addOns.map((id) => luxuryAddOns.find((a) => a.id === id)?.title).join(", ")} />
            )}
            {submitted.priced ? (
              <Row label="Estimated Total" value={`$${submitted.estimatedPrice}`} gold />
            ) : (
              <Row label="Ride Price" value="Custom quote — we'll confirm" gold />
            )}
          </div>
          <p className="mt-5 text-xs text-muted-foreground">A confirmation has been saved. Our team will contact you at {submitted.phone} to finalize details.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button onClick={reset} className="btn-gold rounded-full px-6 py-3 text-sm font-semibold uppercase tracking-widest">Book Another</button>
            <Link to="/" className="btn-ghost-gold rounded-full px-6 py-3 text-sm font-semibold uppercase tracking-widest">Back Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="relative pt-36 pb-14 bg-[#0a121a] border-b border-white/5 overflow-hidden">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-[40rem] rounded-full bg-[var(--gold)]/8 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-5 text-center">
          <p className="reveal font-display text-xs uppercase tracking-[0.4em] text-[var(--gold)] mb-4">Reserve Your Ride</p>
          <h1 className="reveal font-display text-4xl sm:text-5xl font-extrabold text-white">Book Online</h1>
          <p className="reveal mt-4 text-muted-foreground">Tell us about your journey and we'll take care of the rest.</p>
        </div>
      </section>

      <section className="py-16">
        <form onSubmit={submit} className="max-w-3xl mx-auto px-5">
          <div className="reveal rounded-2xl border border-white/10 bg-[#13212e] p-6 sm:p-9 space-y-7">
            {/* Service Type */}
            <div>
              <h2 className="font-display text-xl font-bold text-white">Select Service Type</h2>
              <p className="text-sm text-muted-foreground mt-1">Choose the type of service that fits your needs</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {serviceTypes.map((st) => {
                  const Icon = SERVICE_ICONS[st.icon] || Sparkles;
                  const active = form.service === st.title;
                  return (
                    <button
                      type="button"
                      key={st.id}
                      onClick={() => set("service", st.title)}
                      className={`text-left rounded-xl border p-4 transition-all ${active ? "border-[var(--gold)] bg-[var(--gold)]/5 ring-1 ring-[var(--gold)]/40" : "border-white/10 bg-[#0a121a] hover:border-white/25"}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${active ? "bg-[var(--gold)] text-[#0c1620]" : "bg-[var(--gold)]/10 text-[var(--gold)]"}`}>
                          <Icon size={20} />
                        </div>
                        <div>
                          <div className="font-semibold text-white">{st.title}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{st.desc}</div>
                          {st.note && <div className="text-[11px] text-[var(--gold)] mt-1">{st.note}</div>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {isHourly && (
                <div className="mt-4 max-w-xs">
                  <Label className="mb-2 flex items-center gap-2 text-sm text-gray-200">
                    <Clock size={14} className="text-[var(--gold)]" /> Number of Hours (3 hr minimum)
                  </Label>
                  <Select value={String(hours)} onValueChange={(v) => setHours(parseInt(v, 10))}>
                    <SelectTrigger className="bg-[#0a121a] border-white/15 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {Array.from({ length: 98 }, (_, i) => i + 3).map((h) => (
                        <SelectItem key={h} value={String(h)}>{h} hours</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Contact */}
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" icon={User}>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="John Carter" className="bg-[#0a121a] border-white/15 h-11" />
              </Field>
              <Field label="Email" icon={Mail}>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="bg-[#0a121a] border-white/15 h-11" />
              </Field>
              <Field label="Phone" icon={Phone}>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(786) 000-0000" className="bg-[#0a121a] border-white/15 h-11" />
              </Field>
              <Field label="Passengers" icon={Users}>
                <Select value={form.passengers} onValueChange={(v) => set("passengers", v)}>
                  <SelectTrigger className="bg-[#0a121a] border-white/15 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6].map((n) => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "Passenger" : "Passengers"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {/* Date */}
            <Field label="Pickup Date" icon={CalendarIcon}>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className={`flex h-11 w-full items-center justify-between rounded-md border border-white/15 bg-[#0a121a] px-3 text-sm ${date ? "text-white" : "text-muted-foreground"}`}>
                    {date ? format(date, "EEEE, MMMM d, yyyy") : "Pick a date"}
                    <CalendarIcon size={16} className="text-[var(--gold)]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                </PopoverContent>
              </Popover>
            </Field>

            {/* Pickup / Start Time slots */}
            <div>
              <Label className="mb-3 flex items-center gap-2 text-sm text-gray-200">
                <Clock size={14} className="text-[var(--gold)]" /> Pickup / Start Time
                {form.time && <span className="ml-1 text-[var(--gold)]">· {formatTime(form.time)}</span>}
              </Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-56 overflow-y-auto pr-1 -mr-1">
                {TIME_SLOTS.map((s) => {
                  const active = form.time === s.value;
                  return (
                    <button
                      type="button"
                      key={s.value}
                      onClick={() => set("time", s.value)}
                      className={`rounded-lg border px-2 py-2.5 text-xs font-semibold transition-all ${active ? "border-[var(--gold)] bg-[var(--gold)] text-[#0c1620] shadow-[0_4px_14px_-6px_rgba(242,225,92,0.6)]" : "border-white/15 bg-[#0a121a] text-gray-200 hover:border-[var(--gold)]/60 hover:text-[var(--gold)]"}`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Locations */}
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Pickup Location" icon={MapPin}>
                <Input value={form.pickup} onChange={(e) => set("pickup", e.target.value)} placeholder="Address or MIA / FLL" className="bg-[#0a121a] border-white/15 h-11" />
              </Field>
              <Field label="Drop-off Location" icon={MapPin}>
                <Input value={form.dropoff} onChange={(e) => set("dropoff", e.target.value)} placeholder="Destination address" className="bg-[#0a121a] border-white/15 h-11" />
              </Field>
            </div>

            {/* Add-ons */}
            <div>
              <Label className="text-sm text-gray-200 mb-3 block">Luxury Add-Ons (optional)</Label>
              <div className="grid sm:grid-cols-2 gap-3">
                {luxuryAddOns.slice(0, 6).map((a) => {
                  const checked = addOns.includes(a.id);
                  return (
                    <div
                      key={a.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleAddOn(a.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleAddOn(a.id);
                        }
                      }}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors select-none ${checked ? "border-[var(--gold)]/60 bg-[var(--gold)]/5" : "border-white/10 bg-[#0a121a] hover:border-white/25"}`}
                    >
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${checked ? "bg-[var(--gold)] border-[var(--gold)] text-[#0c1620]" : "border-white/30 text-transparent"}`}>
                        <Check size={14} strokeWidth={3} />
                      </span>
                      <span className="flex-1 text-sm text-gray-200">{a.title}</span>
                      <span className="text-xs font-semibold text-[var(--gold)]">{a.price}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <Field label="Special Requests">
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Flight number, occasion, preferences..." className="bg-[#0a121a] border-white/15 min-h-24" />
            </Field>

            <div className="rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--gold)]">
                    <Sparkles size={14} /> Instant Estimate
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {!form.service
                      ? "Select a service type to see your estimate."
                      : quote?.priced
                      ? `${quote.hours} hrs × $150/hr${quote.addonsTotal ? ` + $${quote.addonsTotal} add-ons` : ""} — final price confirmed by our team.`
                      : quote?.addonsTotal
                      ? `Ride is a custom quote — we'll confirm. Add-ons subtotal shown.`
                      : "This service is quoted individually — we'll confirm your price."}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {quote?.priced ? (
                    <>
                      <div className="font-display text-3xl font-extrabold text-gold-gradient">${quote.total}</div>
                      <div className="text-[11px] text-muted-foreground">estimated total</div>
                    </>
                  ) : quote?.addonsTotal ? (
                    <>
                      <div className="font-display text-3xl font-extrabold text-gold-gradient">${quote.addonsTotal}</div>
                      <div className="text-[11px] text-muted-foreground">add-ons only</div>
                    </>
                  ) : (
                    <div className="font-display text-lg font-bold text-gold-gradient">By Quote</div>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" disabled={submitting} className="btn-gold w-full rounded-full py-4 text-sm font-semibold uppercase tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
              {submitting ? "Submitting..." : "Confirm Booking"} {!submitting && <ArrowRight size={16} />}
            </button>
            <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Check size={13} className="text-[var(--gold)]" /> No payment required now — we'll confirm details with you directly.
            </p>
          </div>
        </form>
      </section>
    </div>
  );
};

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <Label className="mb-2 flex items-center gap-2 text-sm text-gray-200">
      {Icon && <Icon size={14} className="text-[var(--gold)]" />} {label}
    </Label>
    {children}
  </div>
);

const Row = ({ label, value, gold }) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-semibold text-right ${gold ? "text-[var(--gold)]" : "text-white"}`}>{value}</span>
  </div>
);

export default BookOnline;
